import {
  CarOutlined,
  ContactsOutlined,
  DeleteOutlined,
  FileTextOutlined,
  HistoryOutlined,
  IdcardOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Drawer, Menu, Popconfirm, Skeleton, Space, Tag, Tooltip, Typography } from 'antd';
import type { MenuProps } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@tms/shared';

import { api } from '@/api/client';
import { useApiMutation } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { EntityAuditLog } from '@/components/EntityAuditLog';
import { EntityId } from '@/components/EntityId';

import { ContactsSection } from './ContactsSection';
import { DocumentsSection } from './DocumentsSection';
import { DriverSection } from './DriverSection';
import { MainSection } from './MainSection';
import { employeeKey, fullNameOf, type EmployeeDetail } from './types';

type SectionKey = 'main' | 'contacts' | 'documents' | 'driver' | 'audit';

/** Запоминаем, свёрнуто ли меню, — между открытиями карточек. */
const COLLAPSED_KEY = 'tms.employeeCard.menuCollapsed';
const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

interface Props {
  /** id сотрудника, 'new' — приём нового, null — карточка закрыта. */
  employeeId: number | 'new' | null;
  onClose: () => void;
}

/**
 * Карточка сотрудника — выдвижная панель с меню разделов слева.
 *
 * Разделы вместо одной длинной формы: личное дело растёт (документы,
 * переводы, отпуска, табель), и прокрутка всего подряд перестала быть
 * удобной. Список сотрудников остаётся под панелью.
 */
export function EmployeeDrawer({ employeeId, onClose }: Props) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [section, setSection] = useState<SectionKey>('main');
  const [collapsed, setCollapsed] = useState(readCollapsed);
  // После приёма карточка не закрывается, а превращается в карточку нового сотрудника.
  const [createdId, setCreatedId] = useState<number | null>(null);

  const id = createdId ?? (typeof employeeId === 'number' ? employeeId : null);
  const open = employeeId !== null;

  useEffect(() => {
    if (!open) return;
    setSection('main');
    setCreatedId(null);
  }, [employeeId, open]);

  const query = useQuery({
    queryKey: id ? employeeKey(id) : ['employee', 'none'],
    enabled: id !== null,
    queryFn: async () => (await api.get<EmployeeDetail>(`/employees/${id}`)).data,
  });
  const employee = query.data ?? null;

  const remove = useApiMutation(async () => (await api.delete(`/employees/${id}`)).data, {
    successMessage: t('Сотрудник удалён'),
    invalidate: [['employees'], ['office-summary']],
  });

  const toggleCollapsed = (): void => {
    setCollapsed((value) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, value ? '0' : '1');
      } catch {
        /* без сохранения — не страшно */
      }
      return !value;
    });
  };

  const isNew = id === null;
  const items: MenuProps['items'] = [
    { key: 'main', icon: <IdcardOutlined />, label: t('Основное') },
    { key: 'contacts', icon: <ContactsOutlined />, label: t('Контакты'), disabled: isNew },
    { key: 'documents', icon: <FileTextOutlined />, label: t('Документы и допуски'), disabled: isNew },
    ...(employee?.driverProfile && can(PERMISSIONS.DRIVER_READ)
      ? [{ key: 'driver', icon: <CarOutlined />, label: t('Водитель') }]
      : []),
    ...(can(PERMISSIONS.AUDIT_READ)
      ? [{ key: 'audit', icon: <HistoryOutlined />, label: t('Журнал действий'), disabled: isNew }]
      : []),
  ];

  const renderSection = () => {
    if (!isNew && !employee) return <Skeleton active />;
    if (section === 'main' || !employee) {
      return <MainSection employee={employee} onCreated={(newId) => setCreatedId(newId)} />;
    }
    switch (section) {
      case 'contacts':
        return <ContactsSection employee={employee} />;
      case 'documents':
        return <DocumentsSection employee={employee} />;
      case 'driver':
        return employee.driverProfile ? <DriverSection driverId={employee.driverProfile.id} /> : null;
      case 'audit':
        return <EntityAuditLog entity="Employee" entityId={employee.id} limit={50} />;
      default:
        return null;
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="min(1280px, 96vw)"
      destroyOnHidden
      styles={{ body: { padding: 0 } }}
      title={
        employee ? (
          <Space size={8} wrap>
            <span>{fullNameOf(employee)}</span>
            <EntityId id={employee.id} />
            <Tag color={employee.isActive ? 'green' : 'default'} bordered={false}>
              {employee.isActive ? t('Работает') : t('Уволен')}
            </Tag>
            <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
              {[employee.position?.name, employee.department?.name].filter(Boolean).join(' · ') || ''}
            </Typography.Text>
          </Space>
        ) : (
          t('Приём сотрудника')
        )
      }
      extra={
        employee && (
          <Space size={12}>
            <div className="tms-card-stamp">
              <span>{t('Создан / изменён')}</span>
              <span>
                {dayjs(employee.createdAt).format('DD.MM.YYYY')} / {dayjs(employee.updatedAt).format('DD.MM.YYYY HH:mm')}
              </span>
            </div>
            <Tooltip title={t('Обновить')}>
              <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={() => void query.refetch()} />
            </Tooltip>
            {can(PERMISSIONS.EMPLOYEE_DELETE) && (
              <Popconfirm
                title={t('Удалить карточку сотрудника?')}
                description={t('Для увольнения достаточно указать дату увольнения в «Основном».')}
                okText={t('Удалить')}
                cancelText={t('Отмена')}
                onConfirm={() => remove.mutate(undefined, { onSuccess: onClose })}
              >
                <Button danger icon={<DeleteOutlined />}>
                  {t('Удалить')}
                </Button>
              </Popconfirm>
            )}
          </Space>
        )
      }
    >
      <div className="tms-employee-card">
        <aside className={`tms-employee-card-nav${collapsed ? ' is-collapsed' : ''}`}>
          <Button
            type="text"
            block
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={toggleCollapsed}
            className="tms-employee-card-hide"
          >
            {!collapsed && t('Скрыть')}
          </Button>
          <Menu
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={[section]}
            items={items}
            onClick={({ key }) => setSection(key as SectionKey)}
          />
        </aside>
        <main className="tms-employee-card-body">{renderSection()}</main>
      </div>
    </Drawer>
  );
}
